import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, Minus, Plus, ShoppingBag, ArrowRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { useApp } from '../context/AppContext';
import { formatPrice } from '../lib/utils';
import { usePageTitle } from '../hooks/usePageTitle';

export default function CartPage() {
  const { cart, updateCartItem, removeFromCart, clearCart } = useApp();
  const navigate = useNavigate();

  const handleQuantityChange = async (item, newQuantity) => {
    if (newQuantity < 1) {
      await removeFromCart(item.product_id, item.variant_id);
    } else {
      await updateCartItem(item.product_id, newQuantity, item.variant_id);
    }
  };

  const shippingCost = cart.shipping_cost ?? (cart.subtotal >= 100 ? 0 : 10);
  const gstAmount = cart.gst_amount ?? Math.round((cart.subtotal + shippingCost) * 0.15 * 100) / 100;
  const total = cart.total ?? (cart.subtotal + shippingCost + gstAmount);
  usePageTitle('Shopping Cart');

  if (cart.items.length === 0) {
    return (
      <div className="page-transition min-h-[60vh] flex items-center justify-center">
        <div className="text-center max-w-md mx-auto px-4">
          <ShoppingBag className="h-16 w-16 mx-auto text-muted-foreground mb-6" />
          <h1 className="text-2xl font-bold mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
            Your cart is empty
          </h1>
          <p className="text-muted-foreground mb-8">
            Looks like you haven't added any items to your cart yet.
          </p>
          <Link to="/shop" data-testid="empty-cart-shop-link">
            <Button className="bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full px-8">
              Start Shopping
              <ArrowRight className="ml-2 h-4 w-4" />
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="page-transition">
      {/* Header */}
      <div className="bg-[#0B1120] text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="breadcrumb mb-4">
            <Link to="/" className="text-white/60 hover:text-white">Home</Link>
            <span className="text-white/40">/</span>
            <span className="text-white">Shopping Cart</span>
          </nav>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="cart-title">
            Shopping Cart
          </h1>
          <p className="text-white/70 mt-2">{cart.item_count} items</p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Cart Items */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl border border-border overflow-hidden" data-testid="cart-items">
              {cart.items.map((item, index) => (
                <div 
                  key={`${item.product_id}-${item.variant_id}`}
                  className={`p-4 sm:p-6 flex gap-4 sm:gap-6 ${
                    index !== cart.items.length - 1 ? 'border-b border-border' : ''
                  }`}
                  data-testid={`cart-item-${item.product_id}`}
                >
                  {/* Image */}
                  <Link 
                    to={`/product/${item.product_id}`}
                    className="w-24 sm:w-32 h-24 sm:h-32 rounded-lg overflow-hidden flex-shrink-0"
                  >
                    <img
                      src={item.product?.images?.[0] || 'https://images.unsplash.com/photo-1559709451-1b5dc3c0038c?w=200'}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </Link>

                  {/* Details */}
                  <div className="flex-1 min-w-0">
                    <Link to={`/product/${item.product_id}`}>
                      <h3 className="font-medium text-foreground hover:text-[#EA580C] transition-colors line-clamp-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                        {item.product?.name}
                      </h3>
                    </Link>
                    <p className="text-sm text-muted-foreground mt-1">
                      {item.product?.category}
                    </p>
                    {item.variant_label && (
                      <p className="text-sm text-muted-foreground mt-1" data-testid={`cart-item-variant-${item.product_id}`}>
                        {item.variant_label}
                      </p>
                    )}
                    
                    <div className="flex flex-wrap items-center gap-4 mt-4">
                      {/* Quantity */}
                      <div className="quantity-selector">
                        <button 
                          onClick={() => handleQuantityChange(item, item.quantity - 1)}
                          data-testid={`decrease-qty-${item.product_id}`}
                        >
                          <Minus className="h-4 w-4" />
                        </button>
                        <input 
                          type="text" 
                          value={item.quantity} 
                          readOnly 
                          className="font-medium"
                        />
                        <button 
                          onClick={() => handleQuantityChange(item, item.quantity + 1)}
                          data-testid={`increase-qty-${item.product_id}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      {/* Remove */}
                      <button
                        onClick={() => removeFromCart(item.product_id, item.variant_id)}
                        className="text-muted-foreground hover:text-red-500 transition-colors p-2"
                        data-testid={`remove-item-${item.product_id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </div>

                  {/* Price */}
                  <div className="text-right">
                    <p className="font-semibold text-foreground">
                      {formatPrice(item.item_total)}
                    </p>
                    {item.quantity > 1 && (
                      <p className="text-sm text-muted-foreground">
                        {formatPrice(item.unit_price)} each
                      </p>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Clear Cart */}
            <div className="mt-4 flex justify-between items-center">
              <Link to="/shop">
                <Button variant="outline" className="rounded-full">
                  Continue Shopping
                </Button>
              </Link>
              <Button 
                variant="ghost" 
                onClick={clearCart}
                className="text-muted-foreground hover:text-red-500"
                data-testid="clear-cart-btn"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Clear Cart
              </Button>
            </div>
          </div>

          {/* Order Summary */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl border border-border p-6 sticky top-24" data-testid="order-summary">
              <h2 className="text-xl font-bold mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
                Order Summary
              </h2>

              <div className="space-y-3 mb-6">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Subtotal</span>
                  <span className="font-medium">{formatPrice(cart.subtotal)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Shipping</span>
                  <span className="font-medium">
                    {shippingCost === 0 ? (
                      <span className="text-green-600">Free</span>
                    ) : (
                      formatPrice(shippingCost)
                    )}
                  </span>
                </div>
                {cart.subtotal < 100 && (
                  <p className="text-sm text-muted-foreground">
                    Add {formatPrice(100 - cart.subtotal)} more for free shipping
                  </p>
                )}
                <div className="flex justify-between">
                  <span className="text-muted-foreground">GST (15%)</span>
                  <span className="font-medium" data-testid="cart-gst">{formatPrice(gstAmount)}</span>
                </div>
              </div>

              <div className="border-t border-border pt-4 mb-6">
                <div className="flex justify-between">
                  <span className="text-lg font-semibold">Total</span>
                  <span className="text-lg font-bold" data-testid="cart-total">
                    {formatPrice(total)}
                  </span>
                </div>
                <p className="text-sm text-muted-foreground mt-1">
                  Includes {formatPrice(gstAmount)} GST
                </p>
              </div>

              <Button
                onClick={() => navigate('/checkout')}
                className="w-full bg-[#EA580C] hover:bg-[#EA580C]/90 text-white rounded-full py-6 text-lg font-medium btn-primary"
                data-testid="proceed-checkout-btn"
              >
                Proceed to Checkout
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>

              <div className="mt-6 text-center">
                <p className="text-sm text-muted-foreground">
                  Secure checkout powered by Stripe
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
